import React from "react";
import { Flag } from "lucide-react";

/**
 * Golf-specific event configuration block. Renders only when the chosen sport
 * is golf. Captures everything coaches/clubs/promoters need for matches,
 * tournaments, corporate leagues, and Ryder-Cup-style team events.
 *
 * Props:
 *   value:    GolfConfig object (see backend/server.py GolfConfig)
 *   onChange: (newConfig) => void
 *   variant:  "match" | "tournament" | "league" — slightly tunes copy + defaults
 */
export const GOLF_FORMATS = [
  { key: "stroke", label: "Stroke play", desc: "Total strokes; lowest wins. The standard format for tour events." },
  { key: "stableford", label: "Stableford", desc: "Points per hole (eagle 4 / birdie 3 / par 2 / bogey 1). Forgiving format — bad holes don't kill you." },
  { key: "match_play", label: "Match play", desc: "Hole-by-hole; hole won = 1 up. Result like '3&2'. Used in Ryder Cup singles." },
  { key: "scramble", label: "Scramble (team)", desc: "Everyone tees off, pick the best ball, all play from there. Charity-day classic." },
  { key: "best_ball", label: "Best ball / Four-ball", desc: "Each player plays own ball; team takes the lower score per hole. Ryder Cup four-ball." },
  { key: "foursomes", label: "Foursomes (alternate shot)", desc: "Two players, one ball, alternating shots. Hardest team format. Ryder Cup foursomes." },
  { key: "skins", label: "Skins", desc: "Each hole = a 'skin'. Tied holes carry over. Pure side-game energy." },
  { key: "ryder_cup", label: "Ryder Cup (full)", desc: "Multi-day team event: foursomes → four-balls → singles. Set up sub-rounds after creating." },
];

const HANDICAP_DEFAULTS = {
  stroke: 1.0,
  stableford: 0.85,
  match_play: 0.9,
  scramble: 0.35,  // common pair scramble allowance
  best_ball: 0.85,
  foursomes: 0.5,
  skins: 1.0,
  ryder_cup: 0.0,  // off — Ryder Cup is scratch
};
const TEAM_SIZE_DEFAULTS = { scramble: 4, best_ball: 2, foursomes: 2, ryder_cup: 12 };
const SIDE_GAMES = [
  { key: "closest_to_pin", label: "Closest to pin" },
  { key: "longest_drive", label: "Longest drive" },
  { key: "skins", label: "Skins (carry-over)" },
  { key: "nassau", label: "Nassau (front 9 / back 9 / total)" },
];

export default function GolfFormatFields({ value, onChange, variant = "match" }) {
  const cfg = value || {};
  const set = (patch) => onChange({ ...cfg, ...patch });
  const fmt = cfg.format || "stroke";
  const fmtDesc = (GOLF_FORMATS.find((f) => f.key === fmt) || {}).desc;

  // When the format changes, reset team_size + handicap_allowance to sensible defaults
  // (variant-aware: tournaments/leagues default to higher counts).
  const onFormatChange = (newFmt) => {
    const team = TEAM_SIZE_DEFAULTS[newFmt] || 1;
    set({
      format: newFmt,
      handicap_allowance: HANDICAP_DEFAULTS[newFmt] ?? 1.0,
      team_size: team,
    });
  };

  const toggleSide = (k) => {
    const s = new Set(cfg.side_games || []);
    if (s.has(k)) s.delete(k); else s.add(k);
    set({ side_games: [...s] });
  };

  return (
    <div className="rounded-xl border border-[#00E676]/20 bg-[#00E676]/[0.03] p-4 mt-2 space-y-4" data-testid="golf-format-block">
      <div className="flex items-center gap-2 text-[#00E676]">
        <Flag size={16} /> <span className="font-bold uppercase tracking-wider text-xs">Golf format</span>
      </div>

      <Field label="Format">
        <select data-testid="golf-format-select" value={fmt} onChange={(e) => onFormatChange(e.target.value)} className="pp-input">
          {GOLF_FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        {fmtDesc && <p className="text-xs text-zinc-500 mt-1.5" data-testid="golf-format-desc">{fmtDesc}</p>}
      </Field>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Holes">
          <select data-testid="golf-holes" value={cfg.holes || 18} onChange={(e) => set({ holes: Number(e.target.value) })} className="pp-input">
            <option value={9}>9 holes</option>
            <option value={18}>18 holes</option>
            <option value={36}>36 holes</option>
          </select>
        </Field>
        <Field label="Course par">
          <input data-testid="golf-par" type="number" min={54} max={80} value={cfg.course_par || 72}
            onChange={(e) => set({ course_par: Number(e.target.value) })} className="pp-input" />
        </Field>
        <Field label="Tee">
          <select data-testid="golf-tee" value={cfg.tee_color || ""} onChange={(e) => set({ tee_color: e.target.value || null })} className="pp-input">
            <option value="">—</option>
            <option value="black">Black</option>
            <option value="blue">Blue (championship)</option>
            <option value="white">White (men)</option>
            <option value="yellow">Yellow</option>
            <option value="red">Red (forward)</option>
          </select>
        </Field>
      </div>

      {(["scramble", "best_ball", "foursomes", "ryder_cup"].includes(fmt)) && (
        <Field label={fmt === "ryder_cup" ? "Players per team" : "Players per team"}>
          <input data-testid="golf-team-size" type="number" min={2} max={20} value={cfg.team_size || 2}
            onChange={(e) => set({ team_size: Number(e.target.value) })} className="pp-input" />
          <p className="text-xs text-zinc-500 mt-1">
            {fmt === "ryder_cup" ? "Default 12 per side (Ryder Cup standard). Set lower for corporate days." : fmt === "scramble" ? "Standard scrambles run 4 per team." : "Most teams play in pairs."}
          </p>
        </Field>
      )}

      <Field label={`Handicap allowance (${Math.round((cfg.handicap_allowance ?? 1) * 100)}%)`}>
        <input data-testid="golf-handicap" type="range" min={0} max={1} step={0.05}
          value={cfg.handicap_allowance ?? 1.0}
          onChange={(e) => set({ handicap_allowance: Number(e.target.value) })}
          className="w-full" />
        <p className="text-xs text-zinc-500 mt-1">
          {cfg.handicap_allowance === 0 ? "Scratch — no strokes given." :
            cfg.handicap_allowance >= 1 ? "Full handicap (recreational)." :
              `${Math.round(cfg.handicap_allowance * 100)}% — recommended for ${GOLF_FORMATS.find((f) => f.key === fmt)?.label}.`}
        </p>
      </Field>

      <Field label="Side games (optional)">
        <div className="flex flex-wrap gap-2">
          {SIDE_GAMES.map((sg) => {
            const on = (cfg.side_games || []).includes(sg.key);
            return (
              <button key={sg.key} type="button" onClick={() => toggleSide(sg.key)}
                data-testid={`golf-side-${sg.key}`}
                className={`pp-chip text-xs ${on ? "pp-chip-active" : ""}`}>{sg.label}</button>
            );
          })}
        </div>
      </Field>

      {fmt === "ryder_cup" && (
        <p className="text-xs text-[#FFD60A] bg-[#FFD60A]/10 border border-[#FFD60A]/20 p-2 rounded-lg">
          ℹ Ryder Cup is a multi-day team event. After creating the {variant}, use the schedule page to add the foursomes, four-balls, and singles sub-rounds.
        </p>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{label}</div>
      {children}
    </label>
  );
}
